/** Core domain types shared by the Worker API and the React frontend. */

export type PositionRole = 'core' | 'tactical' | 'probe' | 'hedge';
export const POSITION_ROLES: PositionRole[] = ['core', 'tactical', 'probe', 'hedge'];

/** Conviction is an integer 0-5. */
export type Conviction = 0 | 1 | 2 | 3 | 4 | 5;

export interface Position {
  id: string;
  ticker: string;
  /**
   * Share count. Either `shares` or `value` must be set. When both are present
   * `shares` wins and `value` is treated as a stale hint.
   */
  shares: number | null;
  /** Dollar value, for users who think in notional rather than share counts. */
  value: number | null;
  costBasis: number | null;
  role: PositionRole;
  conviction: Conviction;
  /**
   * User-supplied sector, tier 3 of the classification chain. Wins over the
   * bundled map and the live lookup, and is persisted with the portfolio so an
   * Unknown only has to be fixed once.
   */
  sectorOverride: string | null;
  /**
   * Structural leverage of the instrument: 3 for TQQQ, −3 for SQQQ, 1 for
   * ordinary equity. Scales economic notional in gross exposure, and exempts the
   * position from beta cap-and-shrink.
   */
  leverageFactor: number;
}

/** Where a position's sector classification came from. */
export type SectorSource = 'manual' | 'map' | 'live' | 'unknown';

/**
 * The limits a book is measured against. One editable object, persisted with the
 * portfolio, and the single source for every threshold in the app — no engine
 * carries its own hardcoded number any more.
 */
export interface Policy {
  /** Worst tolerable stress-scenario drawdown, as a positive fraction of equity. */
  maxStressDrawdown: number;
  /** Largest permitted single position, on economic notional. */
  maxPositionWeight: number;
  /** Largest permitted sector. */
  maxSectorWeight: number;
  /** Largest permitted gross exposure. */
  maxGross: number;
  /** Target daily portfolio volatility, as a decimal (0.005 = 0.5%). */
  volTargetDaily: number;
  /** Realized vol above this multiple of target counts as a breach. */
  volTolerance: number;
  /** Largest share of gross allowed to sit in one soft cluster. */
  maxClusterWeight: number;
  /** Largest share of portfolio risk allowed in the top three contributors. */
  maxTop3RiskShare: number;
  /** Largest share of the book allowed to be hedge-role positions. */
  maxHedgeShare: number;
  /** Annualized option spend above which a hedge is a "long vol donation". */
  maxHedgeAnnualCost: number;
  /** Average pairwise correlation cutoffs for the two clustering tiers. */
  clusterThresholds: ClusterThresholds;
}

export interface PortfolioSettings {
  /** Account equity. When null, equity is inferred as the sum of position values. */
  equity: number | null;
  /** Every threshold the app enforces. */
  policy: Policy;
  /** How far out of the money index hedges are struck, as a fraction of spot. */
  hedgeOtmPct: number;
  /** Target days to expiry for index hedges. */
  hedgeExpiryDays: number;
  /** Risk-free rate used to price hedges. */
  riskFreeRate: number;
  /**
   * Relative uncertainty applied to beta estimates when sizing trims, which is
   * what turns a single trim number into an honest range.
   */
  betaEstimationBand: number;
  /** Beta-adjusted net bands, keyed by regime. */
  bands: Record<Regime, Band>;
  /** VIX cutoffs between regimes. */
  vixThresholds: { low: number; high: number };
  /** |composite| at or above this flips posture off neutral. */
  postureThreshold: number;
  /** Hours a posture-transition alert suppresses the next one. */
  postureCooldownHours: number;
  /**
   * When false (the default) the posture band shift is *advisory*: it is shown
   * alongside the regime band, but breach status, trims and hedge targets are all
   * computed against the regime band. When true the shifted band is what binds.
   *
   * Advisory by default because posture is a four-signal composite over market
   * conditions, and letting it silently move the limit a book is measured against
   * changes the answer without the user asking it to.
   */
  bindPosture: boolean;
}

export interface Band {
  min: number;
  max: number;
}

export interface ClusterThresholds {
  /** At or above this, names are effectively the same position. */
  hard: number;
  /** At or above this, names co-move enough to converge in a risk-off tape. */
  soft: number;
}

export interface Portfolio {
  id: string;
  name: string;
  settings: PortfolioSettings;
  positions: Position[];
  createdAt: string;
  updatedAt: string;
}

/** Payload accepted by POST /api/portfolio — ids are optional and server-assigned. */
export interface PortfolioInput {
  id?: string;
  name: string;
  settings?: Partial<PortfolioSettings>;
  positions: Array<Omit<Position, 'id'> & { id?: string }>;
}

// ---------------------------------------------------------------------------
// Market data
// ---------------------------------------------------------------------------

export interface Quote {
  ticker: string;
  price: number;
  previousClose: number | null;
  changePct: number | null;
  currency: string | null;
  sector: string | null;
  /** Which tier of the classification chain produced `sector`. */
  sectorSource: SectorSource;
  asOf: string;
}

export interface History {
  ticker: string;
  /** ISO dates, ascending. */
  dates: string[];
  /** Adjusted closes, aligned with `dates`. */
  closes: number[];
}

export interface VixReading {
  level: number;
  asOf: string;
}

/**
 * A beta estimate and the provenance behind it. `adjusted` is the number every
 * risk calculation uses; `raw` is kept so the UI can show its work when the
 * estimate was capped or heavily shrunk.
 */
export interface BetaEstimate {
  ticker: string;
  /** OLS slope vs the benchmark, before capping or shrinkage. */
  raw: number;
  /** `raw` clamped to BETA_CAP. */
  capped: number;
  /** Blume-adjusted beta: 0.67 x capped + 0.33. */
  adjusted: number;
  /** True when the raw estimate hit the cap. */
  wasCapped: boolean;
  /** True when cap-and-shrink was skipped because the leverage is structural. */
  structural: boolean;
  observations: number;
}

// ---------------------------------------------------------------------------
// Exposure engine
// ---------------------------------------------------------------------------

export interface PositionExposure {
  id: string;
  ticker: string;
  shares: number | null;
  price: number | null;
  /** Market value of the holding. */
  value: number;
  /** value x |leverageFactor| — economic notional, what gross counts. */
  exposureValue: number;
  leverageFactor: number;
  /** Market-value weight against equity. Drives net and beta contribution. */
  weight: number;
  /** Economic-notional weight against equity. Drives gross and concentration. */
  exposureWeight: number;
  /** Blume-adjusted beta — the number used in every risk calculation. */
  beta: number | null;
  /** Uncapped, unshrunk OLS estimate, for display alongside `beta`. */
  betaRaw: number | null;
  /** True when the raw estimate was clamped to BETA_CAP before shrinkage. */
  betaCapped: boolean;
  /**
   * True when the raw beta is used unadjusted because the instrument's leverage
   * is structural, not an estimation artifact.
   */
  betaStructural: boolean;
  sector: string;
  sectorSource: SectorSource;
  /** weight x beta — this position's slice of directional risk. */
  betaContribution: number;
  role: PositionRole;
  conviction: Conviction;
  /** Set when the position could not be priced. */
  error?: string;
}

export interface Concentration {
  topWeight: number;
  topTicker: string | null;
  top3Weight: number;
  /** Herfindahl-Hirschman index over position weights (0-1). */
  hhi: number;
  /** 1 / HHI — naive count of independent bets before correlation is considered. */
  effectiveNaiveN: number;
}

export interface WeightBucket {
  key: string;
  weight: number;
  value: number;
  count: number;
}

export type FlagLevel = 'info' | 'warn' | 'danger';

export interface Flag {
  id: string;
  level: FlagLevel;
  title: string;
  detail: string;
}

export interface ExposureResult {
  equity: number;
  grossValue: number;
  netValue: number;
  gross: number;
  net: number;
  betaAdjNet: number;
  /** Positions whose beta could not be computed, excluded from betaAdjNet. */
  betaCoverage: number;
  positions: PositionExposure[];
  sectors: WeightBucket[];
  roles: WeightBucket[];
  convictions: WeightBucket[];
  concentration: Concentration;
  flags: Flag[];
}

// ---------------------------------------------------------------------------
// Market posture engine
// ---------------------------------------------------------------------------

/**
 * PRESS   — conditions reward exposure; the band widens one notch.
 * NEUTRAL — conditions are mixed; the regime band stands.
 * RELEASE — conditions punish exposure; the band tightens one notch.
 *
 * These describe what the tape is currently paying for, not what it will do next.
 */
export type Posture = 'press' | 'neutral' | 'release';

export type PostureComponentId = 'vix' | 'breadth' | 'trend' | 'credit';

/** One measured input, its vote, and the readings behind it. */
export interface PostureComponent {
  id: PostureComponentId;
  label: string;
  /** −1 punishes exposure, 0 neutral, +1 rewards exposure. */
  vote: -1 | 0 | 1;
  /** One-line statement of what was measured, never a forecast. */
  summary: string;
  /** The raw readings, always rendered alongside the vote. */
  readings: PostureReading[];
  /** Set when the component could not be measured; vote is then 0. */
  unavailable?: string;
}

export interface PostureReading {
  label: string;
  /** Formatted value, ready to print. */
  value: string;
  /** Sub-signal vote where one exists, for readings that carry their own sign. */
  vote?: -1 | 0 | 1;
  note?: string;
}

export interface PostureBandShift {
  /** Band implied by the VIX regime alone. */
  base: Band;
  /** Band after the posture shift — the one every downstream number uses. */
  effective: Band;
  /** −1 tightened one notch, 0 unchanged, +1 widened one notch. */
  notches: -1 | 0 | 1;
  /** True when the shift was requested but the ladder was already at its end. */
  clamped: boolean;
  explanation: string;
}

export interface PostureTransition {
  from: Posture | null;
  to: Posture;
  at: string;
  /** False when a real transition was held back by the cooldown. */
  alertEmitted: boolean;
  suppressedByCooldown: boolean;
  cooldownHours: number;
  /** Hours since the last emitted transition alert, when one exists. */
  hoursSinceLast: number | null;
}

export interface PostureResult {
  posture: Posture;
  /** Sum of the four component votes, −4..+4. */
  composite: number;
  components: PostureComponent[];
  band: PostureBandShift;
  /** Plain-language verdict, framed as conditions, never prediction. */
  verdict: string;
  /** Null when posture has not changed, or when there is no prior observation. */
  transition: PostureTransition | null;
  /** The alert a transition produces, for the alert engine to consume. */
  alert: Flag | null;
  flags: Flag[];
}

// ---------------------------------------------------------------------------
// Breadth (input to posture)
// ---------------------------------------------------------------------------

export interface BreadthReading {
  /** Fraction of the measured universe trading above its 50-day average. */
  pctAbove50dma: number;
  pctAbove200dma: number;
  /** (new highs − new lows) / universe size, over a 52-week lookback. */
  netNewHighs: number;
  /** Names actually measured, so thin coverage is visible. */
  universeSize: number;
  source: 'api' | 'computed';
  asOf: string;
}

// ---------------------------------------------------------------------------
// Regime & vol engine
// ---------------------------------------------------------------------------

export type Regime = 'low' | 'moderate' | 'high';

export interface RealizedVol {
  /** Daily standard deviation of portfolio returns, as a decimal. */
  daily20: number | null;
  daily60: number | null;
  /** The window actually used downstream (20d, falling back to 60d). */
  daily: number | null;
  /**
   * Length of the window `daily` came from. Consumers that compare `daily`
   * against any other volatility MUST measure that one over the same window —
   * a 60d single-name vol over a 20d portfolio vol is not a ratio of anything.
   */
  window: number | null;
  annualized: number | null;
  observations: number;
}

export interface RegimeResult {
  vix: number | null;
  vixAsOf: string | null;
  regime: Regime;
  /**
   * The band breach status is computed against, and the one every downstream
   * consumer reads. Equals `baseBand` unless `postureBandBinding` is on.
   */
  band: Band;
  /** Band the VIX regime implies on its own. */
  baseBand: Band;
  /**
   * Band the current posture would imply. Present whenever it differs from
   * `baseBand`, whether or not it binds — advisory means shown, not hidden.
   */
  postureBand: Band | null;
  /** True when `postureBand` is the band that binds rather than just advice. */
  postureBandBinding: boolean;
  /** Null when no posture was supplied. */
  posture: Posture | null;
  realizedVol: RealizedVol;
  volTargetDaily: number;
  /** target / realized, clamped. Null when realized vol is unavailable. */
  exposureMultiplier: number | null;
  /** The same ratio before clamping, so the UI can show what was capped away. */
  exposureMultiplierRaw: number | null;
  exposureMultiplierCapped: boolean;
  betaAdjNet: number;
  /** Signed distance from the band: 0 when inside. */
  bandBreach: number;
  /** Fraction of directional exposure to cut (or add, if negative) to reach the band midpoint. */
  suggestedExposureChange: number;
  verdict: string;
  flags: Flag[];
}

// ---------------------------------------------------------------------------
// Correlation engine
// ---------------------------------------------------------------------------

export type CorrelationWindow = 30 | 90;

export interface CorrelationMatrix {
  window: CorrelationWindow;
  /**
   * Row/column order matches `CorrelationResult.tickers`. `null` marks a pair
   * with too little overlapping history to measure — never silently zero, which
   * would read as "uncorrelated".
   */
  values: Array<Array<number | null>>;
  /** Observations actually used per pair, same indexing as `values`. */
  observations: Array<Array<number>>;
}

/**
 * `hard` — members are effectively the same position.
 * `soft` — members co-move enough to converge when risk comes off.
 * `standalone` — no peer met even the soft threshold.
 */
export type ClusterTier = 'hard' | 'soft' | 'standalone';

export interface Cluster {
  id: string;
  label: string;
  tier: ClusterTier;
  tickers: string[];
  /** Share of gross the cluster represents. */
  weight: number;
  /** Mean pairwise correlation among members; null for a singleton. */
  avgCorrelation: number | null;
  /**
   * On a soft cluster: the hard groupings nested inside it, each already a
   * single bet. Soft clusters are always a coarsening of the hard partition.
   */
  hardSubclusters?: string[][];
}

export interface CorrelationResult {
  tickers: string[];
  matrices: CorrelationMatrix[];
  /** Window used for clustering and the SPY column — 90d unless history is short. */
  primaryWindow: CorrelationWindow;
  /** Correlation to the benchmark over the primary window. */
  spyCorrelation: Record<string, number | null>;
  /** Gross-weighted average correlation to the benchmark. */
  avgSpyCorrelation: number | null;
  /** Hard clusters and standalones — the finest partition. */
  clusters: Cluster[];
  /** Soft clusters and standalones — a coarsening of `clusters`. */
  softClusters: Cluster[];
  /**
   * Correlation-adjusted independent bets. Hard clusters collapse fully; soft
   * clusters collapse partially in proportion to their intra-cluster
   * correlation. This is the headline number.
   */
  effectiveN: number | null;
  /** Hard clusters only, collapsing each to exactly one bet — an upper bound. */
  effectiveNHard: number | null;
  /** 1 / HHI on individual positions, ignoring correlation entirely. */
  naiveN: number;
  /** Per-ticker return observations available, so thin history is visible. */
  observations: Record<string, number>;
  /**
   * Daily volatility per ticker over `primaryWindow` — the same window as the
   * matrices, so a covariance matrix built from the two is internally consistent.
   */
  volatilities: Record<string, number | null>;
  flags: Flag[];
}

// ---------------------------------------------------------------------------
// Risk decomposition
// ---------------------------------------------------------------------------

export interface PositionRisk {
  ticker: string;
  weight: number;
  value: number;
  /** ∂σ_p/∂wᵢ — marginal contribution to risk. Null when uncovered. */
  mcr: number | null;
  /** wᵢ · MCRᵢ. These sum to σ_p, not to variance. */
  riskContribution: number | null;
  /** wᵢ · (Σw)ᵢ. These sum to portfolio variance. */
  varianceContribution: number | null;
  /** riskContribution / σ_p — share of total portfolio risk. */
  riskShare: number | null;
  /** Risk contribution per dollar of position value: the trim-efficiency key. */
  riskPerDollar: number | null;
  volatility: number | null;
  /** Negative MCR: trimming this position would *raise* portfolio risk. */
  isDiversifier: boolean;
}

/**
 * Runtime proof that the decomposition closes. Both identities are checked
 * because the two are easy to confuse: risk contributions sum to σ_p, while the
 * un-normalized wᵢ(Σw)ᵢ terms sum to σ_p².
 */
export interface RiskIdentity {
  portfolioVol: number | null;
  portfolioVariance: number | null;
  sumRiskContributions: number | null;
  sumVarianceContributions: number | null;
  /** |Σ RCᵢ − σ_p| — must be ~0. */
  volResidual: number | null;
  /** |Σ wᵢ(Σw)ᵢ − σ_p²| — must be ~0. */
  varianceResidual: number | null;
  closes: boolean;
}

export interface RiskDecomposition {
  /** Model-implied daily portfolio vol from the covariance matrix. */
  portfolioVol: number | null;
  /**
   * Measured realized vol over its own (shorter) window, for contrast. The two
   * are computed over different windows on purpose and will not match exactly.
   */
  realizedVol: number | null;
  realizedVolWindow: number | null;
  covarianceWindow: CorrelationWindow;
  positions: PositionRisk[];
  /** Share of gross with usable vol and correlation data. */
  coverage: number;
  identity: RiskIdentity;
  flags: Flag[];
}

// ---------------------------------------------------------------------------
// Hedge recommender
// ---------------------------------------------------------------------------

export type HedgeActionKind = 'trim' | 'cluster-degross' | 'index-put' | 'index-put-spread' | 'collar';

export interface Range {
  low: number;
  high: number;
}

export interface OptionLeg {
  side: 'long' | 'short';
  type: 'put' | 'call';
  underlying: string;
  strike: number;
  spot: number;
  expiryDays: number;
  contracts: number;
  /** Per-share premium estimate. */
  premium: number;
  delta: number;
  impliedVol: number;
}

export interface HedgeSizing {
  /** Share counts to sell/hedge, as a range acknowledging beta estimate noise. */
  shares?: Range;
  value: Range;
  fractionOfPosition?: Range;
}

export interface HedgeCost {
  /** Immediate cash cost. 0 for trims; negative is a net credit. */
  premium: number;
  /** Premium annualized against book equity — the long-vol-donation metric. */
  annualizedPctOfBook: number;
  /** Dollars of cost per 1.0 of beta-adjusted net removed. */
  costPerBetaUnit: number | null;
  note: string;
}

export interface HedgeOutcome {
  betaAdjNet: number;
  insideBand: boolean;
  /** Worst applicable stress scenario after the action, as a share of equity. */
  worstStressPct: number | null;
  /** Reduction in model portfolio vol, as a share of the original. */
  riskReduction: number | null;
}

export interface HedgeAction {
  id: string;
  kind: HedgeActionKind;
  rank: number;
  label: string;
  /** Why this action ranks here. */
  rationale: string;
  /** Concrete instruction the user can act on. */
  instruction: string;
  /** What this action costs you if it turns out to be unnecessary. */
  tradeoff: string;
  ticker?: string;
  /** Every position the action touches. Single-name actions list one. */
  tickers?: string[];
  sizing?: HedgeSizing;
  legs?: OptionLeg[];
  cost: HedgeCost;
  resulting: HedgeOutcome;
  /**
   * Ids this action substitutes for rather than adds to — a cluster de-gross is
   * an alternative to trimming its members individually, not an extra step.
   */
  alternativeTo?: string[];
  flags: Flag[];
}

export interface ProtectedPosition {
  ticker: string;
  reason: string;
  /** True when the position actively reduces risk (negative MCR). */
  isDiversifier: boolean;
}

export interface HedgeResult {
  /** Band midpoint the plan targets. */
  targetBetaAdjNet: number;
  currentBetaAdjNet: number;
  /** Beta-adjusted net that must be removed. 0 when already inside the band. */
  requiredReduction: number;
  actions: HedgeAction[];
  /** Positions deliberately left alone, with the reason. */
  protectedPositions: ProtectedPosition[];
  summary: string;
  flags: Flag[];
}

// ---------------------------------------------------------------------------
// Stress test engine
// ---------------------------------------------------------------------------

export interface StressContributor {
  ticker: string;
  pnl: number;
}

export interface StressScenario {
  id: string;
  label: string;
  description: string;
  /** False when the scenario can't be evaluated (e.g. needs options support). */
  applicable: boolean;
  notApplicableReason?: string;
  /** Dollar P&L; negative is a loss. */
  pnl: number;
  /** P&L as a share of equity; negative is a drawdown. */
  pctOfEquity: number;
  breachesTolerance: boolean;
  /** Plain-language statement of how the number was derived. */
  method: string;
  contributors: StressContributor[];
}

export interface StressResult {
  scenarios: StressScenario[];
  worst: { id: string; label: string; pctOfEquity: number } | null;
  maxDrawdown: number;
  /**
   * Undiversified vol / measured portfolio vol. 1.0 means the book already
   * behaves as a single bet; higher means correlation is currently helping.
   */
  diversificationRatio: number | null;
  flags: Flag[];
}

// ---------------------------------------------------------------------------
// Policy checks
// ---------------------------------------------------------------------------

export interface PolicyCheck {
  id: string;
  label: string;
  /** Measured value. */
  actual: number;
  /** The limit from policy. */
  limit: number;
  /** `max` means actual must stay at or below limit; `min` means at or above. */
  direction: 'max' | 'min';
  breached: boolean;
  format: 'pct' | 'mult' | 'count';
  /** One sentence: what this measures and what the breach means. */
  detail: string;
  /** Null when the input could not be measured; the check is then skipped. */
  measurable: boolean;
}

export interface PolicyResult {
  checks: PolicyCheck[];
  breachCount: number;
  /** Ids of the breached checks, in the order they should be addressed. */
  breached: string[];
}

// ---------------------------------------------------------------------------
// Decision
// ---------------------------------------------------------------------------

export type Decision = 'reduce' | 'hold' | 'add';

export interface DecisionResult {
  decision: Decision;
  /** Short label for the card. */
  headline: string;
  /** One line naming the binding constraints, or why nothing binds. */
  reason: string;
  /** Human-readable names of the constraints driving the verdict. */
  bindingConstraints: string[];
}

// ---------------------------------------------------------------------------
// Risk-on playbook
// ---------------------------------------------------------------------------

export interface PlaybookGate {
  id: string;
  label: string;
  passed: boolean;
  /** Formatted measured value. */
  actual: string;
  /** Formatted requirement. */
  required: string;
  /** One line on what the gate is for. */
  explanation: string;
  measurable: boolean;
}

export interface PlaybookResult {
  gates: PlaybookGate[];
  cleared: boolean;
  /** Labels of the failing gates. */
  blockedBy: string[];
  summary: string;
}

// ---------------------------------------------------------------------------
// Vulnerabilities (prose front page)
// ---------------------------------------------------------------------------

export type VulnerabilityKind =
  | 'cluster'
  | 'concentration'
  | 'single-name'
  | 'sector'
  | 'hedge-dependence'
  | 'vol-overage'
  | 'stress'
  | 'data';

export interface Vulnerability {
  id: string;
  kind: VulnerabilityKind;
  /** Named vulnerability, e.g. "Semis cluster". */
  title: string;
  /** A sentence explaining what it is and why it matters. */
  sentence: string;
  /** Share of portfolio risk this accounts for, when measurable. */
  riskShare: number | null;
  /** Share of the book by economic notional, when relevant. */
  weight: number | null;
  severity: FlagLevel;
}

// ---------------------------------------------------------------------------
// Book rows
// ---------------------------------------------------------------------------

export type BookAction = 'trim' | 'hold' | 'protect' | 'trim-if-breach';

export interface BookRow {
  ticker: string;
  role: PositionRole;
  conviction: Conviction;
  /** Economic-notional weight. */
  weight: number;
  beta: number | null;
  betaStructural: boolean;
  riskShare: number | null;
  action: BookAction;
  /** One line on why this tag. */
  actionReason: string;
}

// ---------------------------------------------------------------------------
// Scenarios
// ---------------------------------------------------------------------------

export type ScenarioId = 'current' | 'after-actions' | 'risk-on-ready';

export interface PositionChange {
  ticker: string;
  fromValue: number;
  toValue: number;
  /** Negative when the scenario trims the position. */
  deltaValue: number;
}

/** A full engine run against one book — real or hypothetical. */
export interface ScenarioAnalysis {
  id: ScenarioId;
  label: string;
  /** What this book is and how it was derived. */
  description: string;
  /** True for the book the user actually holds. */
  isCurrent: boolean;
  /** How this book differs from the current one. Empty for `current`. */
  changes: PositionChange[];
  exposure: ExposureResult;
  regime: RegimeResult;
  correlation: CorrelationResult;
  risk: RiskDecomposition;
  stress: StressResult;
  hedge: HedgeResult;
  policy: PolicyResult;
  playbook: PlaybookResult;
  decision: DecisionResult;
  vulnerabilities: Vulnerability[];
  book: BookRow[];
}

// ---------------------------------------------------------------------------
// Snapshots and change detection
// ---------------------------------------------------------------------------

/** One recorded observation of a portfolio, written by the scheduled run. */
export interface Snapshot {
  id: string;
  portfolioId: string;
  takenAt: string;
  /** UTC date of the run. Two runs a day share a date but not a session. */
  session: string;
  decision: Decision;
  betaAdjNet: number;
  realizedVol: number | null;
  worstStress: number | null;
  postureComposite: number;
  /** Component id -> vote, so a flip can be attributed to a measurement. */
  postureVotes: Record<string, number>;
  posture: Posture;
  breachCount: number;
  breachedIds: string[];
  failedGateCount: number;
  failedGateIds: string[];
  top3RiskShare: number | null;
  /** Ids of every flag raised, for set-difference against the next run. */
  flagIds: string[];
  /** Hard-cluster membership, so a cluster hardening is detectable. */
  hardClusters: string[][];
  /** Change ids emitted at this observation, for cooldown accounting. */
  emittedChangeIds: string[];
}

/** Trailing series for the sparkline strip. */
export interface SnapshotSeries {
  takenAt: string[];
  betaAdjNet: number[];
  realizedVol: Array<number | null>;
  worstStress: Array<number | null>;
  postureComposite: number[];
}

export type ChangeKind =
  | 'verdict'
  | 'band'
  | 'posture'
  | 'breach-new'
  | 'breach-cleared'
  | 'cluster-hardened'
  | 'gate';

export interface Change {
  id: string;
  kind: ChangeKind;
  /** One clause, joined into the "what changed" sentence. */
  sentence: string;
  severity: FlagLevel;
  /** True when the cooldown held this back from the headline sentence. */
  suppressed: boolean;
}

export interface ChangeSet {
  /** The observation compared against; null on the first ever run. */
  comparedTo: string | null;
  /** Hours between that observation and now. */
  hoursSince: number | null;
  changes: Change[];
  /** Changes that survived the cooldown, in severity order. */
  material: Change[];
  /** One sentence for the decision card. Says so plainly when nothing moved. */
  sentence: string;
}

/** A recorded transition, for the Changes view. */
export interface ChangeLogEntry {
  takenAt: string;
  changes: Change[];
}

// ---------------------------------------------------------------------------
// Analyze payload
// ---------------------------------------------------------------------------

export interface AnalyzeResult {
  portfolioId: string;
  asOf: string;
  /** Fully-resolved settings the engines actually ran with, defaults merged in. */
  settings: PortfolioSettings;
  /** Market-wide, so it is the same across every scenario. */
  posture: PostureResult;
  /**
   * The full engine chain run once per scenario, so every panel reflects the
   * selected book rather than showing a delta column against the current one.
   */
  scenarios: ScenarioAnalysis[];
  /** Trailing snapshot history for the sparkline strip. Empty until the cron runs. */
  history: SnapshotSeries;
  /** What moved since the last observation from a prior session. */
  changed: ChangeSet;
  /** Recent transitions, newest first, for the Changes view. */
  changeLog: ChangeLogEntry[];
  /** Non-fatal problems: unpriced tickers, missing history, provider fallbacks. */
  warnings: string[];
}

export interface ApiError {
  error: string;
  detail?: string;
}
